import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import {
    WB_ModelCannibalizationRelationshipRow,
    WB_ModelCannibalizationTermRelationshipRow,
    WB_ModelCannibalizationTypeRow,
    WB_ModelFunctionRow,
    WB_ModelingSessionCannMRRow,
    WB_ModelingSessionRow,
    WB_ModelPredictorRelationshipRow,
    WB_ModelPredictorRow,
    WB_ModelProfilePredictorRow,
    WB_ModelProfileGroupPredictorRow,
    WB_ModelProfileGroupRow,
    WB_ModelProfileRow,
    WB_ModelRow,
    WB_ModelTagRelationshipRow,
    WB_ModelTermRelationshipRow,
    WB_ModelTermRow,
    WB_ModelVolumeComponentItemRow,
    WB_ModelVolumeComponentRelationshipRow,
    WB_ModelVolumeComponentRow,
    WB_PowerMetricRelationshipRow,
    WB_TagRow,
    WB_ModelProfileSpendFunctionRow,
    WB_SpendFunctionRow,
    WB_ModelEventTypeRelationshipRow
} from "../PA.Core/PAdbSchemaRows";
import { IwbModel } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Model,
    esWB_Model_Cannibalization_Relationship,
    esWB_Model_Cannibalization_Term_Relationship,
    esWB_Model_Cannibalization_Type,
    esWB_Model_DataSource,
    esWB_Model_Function,
    esWB_Model_Predictor,
    esWB_Model_Predictor_Relationship,
    esWB_Model_Profile,
    esWB_Model_Profile_Predictor,
    esWB_Model_Profile_Group,
    esWB_Model_Profile_Group_Predictor,
    esWB_Model_Tag_Relationship,
    esWB_Model_Term,
    esWB_Model_Term_Relationship,
    esWB_Model_Volume_Component,
    esWB_Model_Volume_Component_ItemR,
    esWB_Model_Volume_ComponentR,
    esWB_Modeling_Session,
    esWB_Modeling_Session_Cannibalization_Type_Relationship,
    esWB_Power_Metric_Relationship,
    esWB_Tag,
    esWB_Model_Profile_Spend_Function, esWB_Model_Event_Type_Relationship, esWB_Spend_Function
} from "../PA.Core/PAdbSchema";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { BehaviorSubject } from "rxjs";
import {
    CannTermChangeEvent,
    IHaveModel,
    IHaveModelPredictors,
    IHaveModelTags,
    IModel,
    ModelChangeEvent,
    ModelTermChangeEvent
} from "../PA.Model.Core/PA.Model.IF.Aspects";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { ModelBOValidations } from "./ModelBO.Validations";
import { LeaseRegistry } from "../PA.Utils/LeaseRegistry";


const ComponentTypeToSourceMapping: { [type: string]: string } = {
    self: "UserEntered",
    constant: "IdentityValue",
    predictor: "Predictor"
};

export interface IModelProfileChanged {
    ModelBO: ModelBO
    Row: WB_ModelProfileRow
    PropertyChanged: string
    PropertyValue: any
}

export class ModelBO implements IBusinessObject, IModel, IHaveModel, IHaveModelPredictors, IHaveModelTags {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Model: WB_ModelRow;


    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Model" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);
        this.drWB_Model = null;

        this.IModel_init();
        this.IHaveModelPredictors_init();
        this.IHaveModelTags_init();
        this.IHaveVolumeComponents_init();
        this.IHaveProfiles_init();
        this.IHaveEventTypes_init();
        this.TermComponents_init();
        this.ProfilePredictor_init();
        this.ProfileGroupCollection_init();
    }

    get WB_Model(): WB_ModelRow {
        return this.drWB_Model;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Model");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for new Model.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext

            self.dbContext
                .createRecordWithKey(esWB_Model)
                .then((newModelRow: WB_ModelRow) => {
                    newModelRow.setDefaults();
                    self.drWB_Model = newModelRow;
                    self.dbContext.add(esWB_Model, newModelRow);
                    self.Identity.setAll(
                        self.WB_Model.cif.WB_MODEL_ID,
                        self.WB_Model.cif.MODEL_ID,
                        self.WB_Model.cif.MODEL_NAME
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                    self.Identity.listenToRecordUpdates(self.WB_Model, "WB_MODEL_ID", "MODEL_ID", "MODEL_NAME");

                    return self._AfterBoLoadCompleteInternal();
                })
                .then((ignore) => {
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    Load(anId: number, options?: object): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");
        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                const sError = "Model already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null) self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for Model.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = ModelBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = ModelBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function() {
                    const newModelRow: any = recordResolver.get(esWB_Model, anId);

                    if (_.isEmpty(newModelRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_Model = newModelRow;
                    self.Identity.setAll(
                        self.WB_Model.cif.WB_MODEL_ID,
                        self.WB_Model.cif.MODEL_ID,
                        self.WB_Model.cif.MODEL_NAME
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                    self.Identity.listenToRecordUpdates(self.WB_Model, "WB_MODEL_ID", "MODEL_ID", "MODEL_NAME");
                    return self._AfterBoLoadCompleteInternal();
                })
                .then((newModelRow: any) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();

                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return this._OnLoadComplete;
    }

    async _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this;

        const tasks: Promise<any>[] = [];
        self.LoadProgressProvider.setProgressMessage("Model.PostLoad", "Processing Loaded Data");
        tasks.push(self.IModel_OnLoadComplete());
        tasks.push(self.IHaveProfiles_onLoadCompelte());
        await Promise.all(tasks);

        await self.maintainModelComponents();
        await self.IHaveModelPredictors_OnReloaded();
        await self.syncProfilePredictors();
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        try {
            this.ValidateMessageProvider.clearMessages();
            this.ValidateProgressProvider.start(
                "Validating " + self.WB_Model.cif.MODEL_NAME + "(" + self.WB_Model.cif.MODEL_ID + ")"
            );

            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                const sError = "Unable to determine dbContext to Validate Model.";
                self.ValidateProgressProvider.exception(sError);
                throw new Error(sError);
            }
            if (!self.WB_Model.isActive) {
                self._bIsValidated = true;
                self.ValidateProgressProvider.finish();
                return self;
            }
            await ModelBOValidations.Validation(self);

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();
            return self;
        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
            return self;
        }
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbModel = <IwbModel>self.drWB_Model;

        wbModel.PRODUCT_LEVEL = wbModel.PRODUCT_TYPE == "Product" ? "Product" : wbModel.PRODUCT_LEVEL;
        this.SaveProgressProvider.start("Saving " + wbModel.MODEL_NAME + "(" + wbModel.MODEL_ID + ")");


        if (_.isEmpty(self.dbContext)) {
            self._bIsSaved = false;
            const sError = "Unable to determine dbContext to Save Model.";
            self.SaveProgressProvider.exception(sError);
            throw sError;
        }

        self.SaveOptions = options || {};


        _.forEach(self.drWB_Model.WB_ModelCannibalizationRelationshipRows, (aRow) => {
            if (_.isEmpty(aRow.WB_ModelFunctionRow)) {
                aRow.cif.WB_MODEL_COEFFICIENT_ID = 0;
                aRow.cif.WB_MODEL_COEFFICIENT_ID2 = 0;
            } else if (aRow.WB_ModelFunctionRow.cif.COEFFICIENT_COUNT == 1) aRow.cif.WB_MODEL_COEFFICIENT_ID2 = 0;
        });

        await self.createDefaultProfiles()


        let saveFunction: (
            esName: string,
            saveRecords: ESRecord[] | ESRecord,
            options?: any
        ) => Promise<ESDataStore>;
        if (bBatch) {
            saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
        } else {
            saveFunction = self.dbContext.Save.bind(self.dbContext);
        }


        if (options?.Validate?.bSkipAllValidations) {
            self._bIsValidated = true;
        } else {
            await self.Validate({});
        }


        if (!self._bIsValidated) {
            self._bIsSaved = false;
            self.SaveProgressProvider.finish();
            return self;
        }

        await saveFunction(esWB_Model, self.drWB_Model, {});

        const tasks: Promise<any>[] = [];
        tasks.push(
            saveFunction(esWB_Model_Term_Relationship, self.drWB_Model.WB_ModelTermRelationshipRows)
        );
        tasks.push(
            saveFunction(
                esWB_Model_Cannibalization_Relationship,
                self.drWB_Model.WB_ModelCannibalizationRelationshipRows
            )
        );
        tasks.push(
            saveFunction(
                esWB_Model_Cannibalization_Term_Relationship,
                _.flatMap(
                    self.drWB_Model.WB_ModelCannibalizationRelationshipRows,
                    (a: WB_ModelCannibalizationRelationshipRow) =>
                        a.WB_ModelCannibalizationTermRelationshipRows
                )
            )
        );
        tasks.push(
            saveFunction(
                esWB_Model_Predictor_Relationship,
                self.drWB_Model.WB_ModelPredictorRelationshipRows
            )
        );
        tasks.push(saveFunction(esWB_Model_Tag_Relationship, self.drWB_Model.WB_ModelTagRelationshipRows));
        tasks.push(
            saveFunction(
                esWB_Model_Volume_ComponentR,
                self.drWB_Model.WB_ModelVolumeComponentRelationshipRows
            )
        );
        tasks.push(
            saveFunction(
                esWB_Model_Volume_Component_ItemR,
                _.flatMap(
                    self.drWB_Model.WB_ModelVolumeComponentRelationshipRows,
                    (a) => a.WB_ModelVolumeComponentItemRows
                )
            )
        );
        tasks.push(saveFunction(esWB_Model_Event_Type_Relationship, self.drWB_Model.WB_ModelEventTypeRelationshipRows));
        tasks.push(saveFunction(esWB_Model_Profile, self.drWB_Model.WB_ModelProfileRows));
        tasks.push(
            saveFunction(
                esWB_Model_Profile_Predictor,
                _.flatMap(self.drWB_Model.WB_ModelProfileRows, (a) => a.WB_ModelProfilePredictorRows)
            )
        );

        _.forEach(self.drWB_Model.WB_ModelProfileRows, (profile) => {
            _.forEach(profile.WB_ModelProfileGroupRows, (group) => {
                tasks.push(saveFunction(esWB_Model_Profile_Group, group));
                _.forEach(group.WB_ModelProfileGroupPredictorRows, (predictor) => {
                    tasks.push(saveFunction(esWB_Model_Profile_Group_Predictor, predictor));
                });
            });

            _.forEach(profile.WB_ModelProfileSpendFunctionRows, (spendFunction) => {
                tasks.push(saveFunction(esWB_Model_Profile_Spend_Function, spendFunction));
            });
        });

        await Promise.all(tasks);

        if (bBatch) {
            self.dbContext.addBatchCompleteListener(() => {
                self._bIsSaved = true;
                LeaseRegistry.generateLeaseInvalidationByClass(
                    LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model, wbModel.WB_MODEL_ID)
                );
            });
        } else {
            self._bIsSaved = true;
            LeaseRegistry.generateLeaseInvalidationByClass(
                LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model, wbModel.WB_MODEL_ID)
            );
        }
        self.SaveProgressProvider.finish();
        return self;

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbModel = <IwbModel>self.drWB_Model;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbModel.MODELL_NAME + "(" + wbModel.MODEL_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {
            // 1. Validate if we can delete the Model

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Model.markDeleted();
            _.forEach(self.drWB_Model.WB_ModelTermRelationshipRows, (a) => a.markDeleted());
            _.forEach(self.drWB_Model.WB_ModelPredictorRelationshipRows, (a) => a.markDeleted());
            _.forEach(self.drWB_Model.WB_ModelTagRelationshipRows, (a) => a.markDeleted());
            _.forEach(self.drWB_Model.WB_ModelVolumeComponentRelationshipRows, (a) => {
                a.markDeleted();
                _.forEach(a.WB_ModelVolumeComponentItemRows, (b) => b.markDeleted());
            });
            _.forEach(
                self.drWB_Model.WB_ModelCannibalizationRelationshipRows,
                (a: WB_ModelCannibalizationRelationshipRow) => {
                    a.markDeleted();
                    _.forEach(a.WB_ModelCannibalizationTermRelationshipRows, (b) => b.markDeleted());
                }
            );
            _.forEach(self.drWB_Model.WB_ModelProfileRows, (profile) => {
                profile.markDeleted();
                _.forEach(profile.WB_ModelProfilePredictorRows, (predictor) => {
                    predictor.markDeleted();
                });
                _.forEach(profile.WB_ModelProfileGroupRows, (group) => {
                    group.markDeleted();
                    _.forEach(group.WB_ModelProfileGroupPredictorRows, (predictor) => {
                        predictor.markDeleted();
                    });
                });

                _.forEach(profile.WB_ModelProfileSpendFunctionRows, (spendFunction) => {
                    spendFunction.markDeleted();
                });
            });

            if (bSaveNow) {
                self.SaveUsing(false, { Validate: { bSkipAllValidations: true } })
                    .then((ignore) => {
                        if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                            return;
                        }
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc = "IModel">

    private _ModelPropertyChanged: BehaviorSubject<ModelChangeEvent>;
    private _ModelTermPropertyChanged: BehaviorSubject<ModelTermChangeEvent>;
    private _ModelTermCollectionChanged: BehaviorSubject<IModel>;
    private _CannTermPropertyChanged: BehaviorSubject<CannTermChangeEvent>;
    private _CannTermCollectionChanged: BehaviorSubject<IModel>;
    private _CannibalizationTypeRows: WB_ModelCannibalizationTypeRow[];

    private IModel_init() {
        this._ModelPropertyChanged = new BehaviorSubject<ModelChangeEvent>(undefined);
        this._ModelTermPropertyChanged = new BehaviorSubject<ModelTermChangeEvent>(undefined);
        this._ModelTermCollectionChanged = new BehaviorSubject<IModel>(undefined);
        this._CannTermPropertyChanged = new BehaviorSubject<CannTermChangeEvent>(undefined);
        this._CannTermCollectionChanged = new BehaviorSubject<IModel>(undefined);
        this._CannibalizationTypeRows = [];
    }

    private async IModel_OnLoadComplete(): Promise<void> {
        const self = this;

        self.WB_Model.setupOnChangeEvents([], self.IModel_onRecordChange.bind(self), self);

        const wbCannibaliztaionTypes: WB_ModelCannibalizationTypeRow[] = await self.dbContext.findAll(
            esWB_Model_Cannibalization_Type,
            { DELETED_IND: { neq: ["1"] } }
        );
        self._CannibalizationTypeRows = wbCannibaliztaionTypes || [];
    }

    private IModel_BeforeReload(): void {
        // Do nothing
    }

    private IModel_onRecordChange(record: WB_ModelRow, propertyName: string, propertyValue: any, options: ModelBO) {
        this.OnModelPropertyChanged.next({
            ModelBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        });
    }

    get CannibalizationTypeRows(): WB_ModelCannibalizationTypeRow[] {
        return this._CannibalizationTypeRows;
    }

    get OnModelTermCollectionChanged(): BehaviorSubject<IModel> {
        return this._ModelTermCollectionChanged;
    }

    get OnCannTermCollectionChanged(): BehaviorSubject<IModel> {
        return this._CannTermCollectionChanged;
    }

    get OnModelTermPropertyChanged(): BehaviorSubject<ModelTermChangeEvent> {
        return this._ModelTermPropertyChanged;
    }

    get OnCannTermPropertyChanged(): BehaviorSubject<CannTermChangeEvent> {
        return this._CannTermPropertyChanged;
    }

    get OnModelPropertyChanged(): BehaviorSubject<ModelChangeEvent> {
        return this._ModelPropertyChanged;
    }

    async addModelTerm(aWbModelTermId: number): Promise<WB_ModelTermRelationshipRow> {
        const self = this;

        // Is the term in the term table?
        const aModelTermRow: WB_ModelTermRow = <any>(
            await self.dbContext.find(esWB_Model_Term, aWbModelTermId, { with: [esWB_Model_Function] })
        );

        if (!aModelTermRow) {
            // Bad Model Term ID
            return;
        }

        // create new records
        const aNewRow: WB_ModelTermRelationshipRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Model_Term_Relationship)
        );
        if (!aNewRow) {
            return;
        }

        aNewRow.setDefaults();
        aNewRow.cif.WB_MODEL_ID = self.drWB_Model.cif.WB_MODEL_ID;
        aNewRow.cif.WB_MODEL_TERM_ID = aWbModelTermId;
        aNewRow.WB_ModelTermRow = aModelTermRow;
        self.dbContext.add(esWB_Model_Term_Relationship, aNewRow);

        self.OnModelTermCollectionChanged.next(self);
    }

    async removeModelTermRow(aWbModelTermRelId: number): Promise<void> {
        const self = this;

        _.forEach(self.WB_Model.WB_ModelTermRelationshipRows, (aModelTermRelRow: WB_ModelTermRelationshipRow) => {
            if (aModelTermRelRow.isActive && aModelTermRelRow.cif.WB_MODEL_TERMR_ID == aWbModelTermRelId) {
                aModelTermRelRow.markDeleted();
            }
        });
        self.OnModelTermCollectionChanged.next(self);
    }

    async addCannTypeRow(wbModelCannTypeId: number): Promise<WB_ModelCannibalizationRelationshipRow> {
        const self = this;

        for (const aRow of self.WB_Model.WB_ModelCannibalizationRelationshipRows) {
            if (!aRow.isActive) continue;

            if (aRow.cif.WB_MODEL_CANNIBALIZATIONM_ID == wbModelCannTypeId) return aRow;
        }

        const aModelCannTypeRow: WB_ModelCannibalizationTypeRow = <any>(
            await self.dbContext.find(esWB_Model_Cannibalization_Type, wbModelCannTypeId)
        );

        const aModelVolumeComponentRow: WB_ModelVolumeComponentRow = <any>(
            await self.dbContext.find(esWB_Model_Volume_Component, aModelCannTypeRow.cif.WB_MODEL_VOLUME_COMPONENT_ID)
        );

        if (_.isEmpty(aModelCannTypeRow)) return;

        const aModelCannTypeRRow: WB_ModelCannibalizationRelationshipRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Model_Cannibalization_Relationship)
        );

        aModelCannTypeRRow.setDefaults();
        aModelCannTypeRRow.cif.WB_MODEL_ID = self.WB_Model.cif.WB_MODEL_ID;
        aModelCannTypeRRow.cif.EVALUATION_ORDER =
            _.filter(self.WB_Model.WB_ModelCannibalizationRelationshipRows, (aRow) => aRow.isActive).length + 1;
        aModelCannTypeRRow.cif.WB_MODEL_CANNIBALIZATIONM_ID = aModelCannTypeRow.cif.WB_MODEL_CANNIBALIZATIONM_ID;
        aModelCannTypeRRow.WB_ModelCannibalizationTypeRow = aModelCannTypeRow;
        aModelCannTypeRRow.WB_ModelCannibalizationTypeRow.WB_Model_Volume_ComponentRow = aModelVolumeComponentRow;
        self.dbContext.add(esWB_Model_Cannibalization_Relationship, aModelCannTypeRRow);
        self.OnCannTermCollectionChanged.next(self);
        return aModelCannTypeRRow;
    }

    async removeCannTypeRow(wbModelCannTypeRelId: number): Promise<void> {
        const self = this;

        for (const aRow of self.WB_Model.WB_ModelCannibalizationRelationshipRows) {
            if (!aRow.isActive) continue;

            if (aRow.cif.WB_MODEL_CANNR_ID == wbModelCannTypeRelId) {
                aRow.markDeleted();

                _.forEach(aRow.WB_ModelCannibalizationTermRelationshipRows, (aCRow) => {
                    aCRow.markDeleted();
                });
            }
        }

        _.forEach(
            _.orderBy(
                _.filter(self.WB_Model.WB_ModelCannibalizationRelationshipRows, (aRow) => aRow.isActive),
                "EVALUATION_ORDER"
            ),
            (aRow, idx) => {
                aRow.cif.EVALUATION_ORDER = idx;
            }
        );

        self.OnCannTermCollectionChanged.next(self);
    }

    getCannTypeRelRowSync(wbModelCannTypeId: number): WB_ModelCannibalizationRelationshipRow {
        const self = this;

        for (const aRow of self.WB_Model.WB_ModelCannibalizationRelationshipRows) {
            if (!aRow.isActive) continue;

            if (aRow.cif.WB_MODEL_CANNIBALIZATIONM_ID == wbModelCannTypeId) return aRow;
        }

        return undefined;
    }

    async getPredictorsRequiringRefValues(): Promise<WB_ModelPredictorRow[]> {
        const self = this;
        const powerMetricIds: number[] = [];
        const distinctPredictors = [];
        _.forEach(
            self.WB_Model.WB_ModelCannibalizationRelationshipRows,
            (aCanTypeRow: WB_ModelCannibalizationRelationshipRow) => {
                if (aCanTypeRow.isActive) powerMetricIds.push(aCanTypeRow.cif.WB_MODEL_POWERMETRIC_ID);
            }
        );

        const powerMetricRelRows = await self.dbContext.findAll(esWB_Power_Metric_Relationship, {
            where: {
                WB_MODEL_POWERMETRIC_ID: { in: powerMetricIds },
                DELETED_IND: { nin: ["1"] }
            }
        });
        const mdlFnIds = _.map(
            powerMetricRelRows,
            (aMdlFnRow: WB_PowerMetricRelationshipRow) => aMdlFnRow.cif.WB_MODEL_FUNCTION_ID
        );
        const mdlFnRows = await self.dbContext.findAll(esWB_Model_Function, {
            where: { WB_MODEL_FUNCTION_ID: { in: mdlFnIds } }
        });
        const refReqMdlFns = _.filter(
            mdlFnRows,
            (mdlFn: WB_ModelFunctionRow) => mdlFn.cif.REFERENCE_VALUE_REQD_IND == "1"
        );
        if (!refReqMdlFns.length) {
            return [];
        }

        const dctRqdMdlFns = _.keyBy(refReqMdlFns, (aMdlFn) => aMdlFn.cif.WB_MODEL_FUNCTION_ID);
        const modelFunctionPwrRelRows = _.filter(
            powerMetricRelRows,
            (a: WB_PowerMetricRelationshipRow) => a.cif.WB_MODEL_FUNCTION_ID in dctRqdMdlFns
        );
        const mdlFnPredictorIds = _.map(
            modelFunctionPwrRelRows,
            (aRow: WB_PowerMetricRelationshipRow) => aRow.cif.WB_MODEL_PREDICTOR_ID
        );
        if (!mdlFnPredictorIds.length) {
            return [];
        }
        return await self.dbContext.findAll(esWB_Model_Predictor, {
            where: { WB_MODEL_PREDICTOR_ID: { in: mdlFnPredictorIds } }
        });
    }

    async getCannibalizationTypeRecords(): Promise<WB_ModelCannibalizationTypeRow[]> {
        const self = this;
        const wbModelIds = _.map(
            self.WB_Model.WB_ModelTermRelationshipRows,
            (mdlTermRow: WB_ModelTermRelationshipRow) => mdlTermRow.cif.WB_MODEL_ID
        );
        if (wbModelIds.length) {
            return [];
        }
        const mdlSessionRecords: WB_ModelingSessionRow[] = await self.dbContext.findAll(esWB_Modeling_Session, {
            where: { WB_MODELING_SESSION_ID: { in: wbModelIds } }
        });
        const mdlSessionIds = _.map(
            mdlSessionRecords,
            (mdlSessionRow: WB_ModelingSessionRow) => mdlSessionRow.cif.WB_MODELING_SESSION_ID
        );
        const mdlSessionCannMRRows: WB_ModelingSessionCannMRRow[] = await self.dbContext.findAll(
            esWB_Modeling_Session_Cannibalization_Type_Relationship,
            { where: { WB_MODELING_SESION_ID: { in: mdlSessionIds } } }
        );
        if (mdlSessionCannMRRows.length) {
            return [];
        }
        const mdlSessionCannMRRIds = _.map(
            mdlSessionCannMRRows,
            (mdlSessionCannMRow: WB_ModelingSessionCannMRRow) => mdlSessionCannMRow.cif.WB_MODEL_CANNIBALIZATIONM_ID
        );
        return await self.dbContext.findAll(esWB_Model_Cannibalization_Type, {
            where: { WB_MODEL_CANNIBALIZATIONM_ID: { in: mdlSessionCannMRRIds } }
        });
    }

    async addCannTerm(
        wbModelCannTypeId: number,
        aWbModelTermId: number
    ): Promise<WB_ModelCannibalizationTermRelationshipRow> {
        const self = this;

        // find/add Cann Term
        const aCannTypeRRow: WB_ModelCannibalizationRelationshipRow = <any>await self.addCannTypeRow(wbModelCannTypeId); // gets or add..

        if (!aCannTypeRRow) return;

        let aModelTermRow: WB_ModelTermRow = undefined;

        // Is the term in the term table?
        aModelTermRow = await self.dbContext.find(esWB_Model_Term, aWbModelTermId, { with: [esWB_Model_Function] });

        if (!aModelTermRow) {
            // Bad Model Term
            return;
        }

        // create new records
        const aNewRow: WB_ModelCannibalizationTermRelationshipRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Model_Cannibalization_Term_Relationship)
        );
        if (!aNewRow) {
            return;
        }

        aNewRow.setDefaults();
        aNewRow.cif.WB_MODEL_CANNR_ID = aCannTypeRRow.cif.WB_MODEL_CANNR_ID;
        aNewRow.cif.WB_MODEL_TERM_ID = aWbModelTermId;
        aNewRow.WB_ModelTermRow = aModelTermRow;
        self.dbContext.add(esWB_Model_Cannibalization_Term_Relationship, aNewRow);

        self.OnCannTermCollectionChanged.next(self);

        return aNewRow;
    }

    async removeCannTermRow(aWbCannTermRelId: number): Promise<void> {
        const self = this;

        _.forEach(self.WB_Model.WB_ModelCannibalizationRelationshipRows, (aCanTypeRow) => {
            _.forEach(
                aCanTypeRow.WB_ModelCannibalizationTermRelationshipRows,
                (aCannTermRelRow: WB_ModelCannibalizationTermRelationshipRow) => {
                    if (aCannTermRelRow.isActive && aCannTermRelRow.cif.WB_MODEL_CANNTERMR_ID == aWbCannTermRelId) {
                        aCannTermRelRow.markDeleted();
                    }
                }
            );
        });

        self.OnCannTermCollectionChanged.next(self);
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveModel">

    get Model(): ModelBO {
        return this;
    }

    get OnIHaveModelLoaded(): Promise<void> {
        return ASync.resolve();
    }

    get bIHaveModelLoaded(): boolean {
        return this.bIsLoaded;
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveModelPredictors operations">

    private _ModelPredictorUIOptions: { [wbPredictorId: number]: { UIGroupName: string; ItemSortOrder: number } };
    private _ModelPredictors: WB_ModelPredictorRow[];
    private _OnIHaveModelPredictorsLoaded: Promise<void>;
    private _OnModelPredictorsChanged: BehaviorSubject<IHaveModelPredictors>;
    private _bIHaveModelPredictorsLoaded: boolean;

    private IHaveModelPredictors_init() {
        //
        this._bIHaveModelPredictorsLoaded = false;
        this._ModelPredictorUIOptions = {};
        this._ModelPredictors = [];
        this._OnIHaveModelPredictorsLoaded = undefined;
        this._OnModelPredictorsChanged = new BehaviorSubject<IHaveModelPredictors>(undefined);
    }

    private IHaveModelPredictors_OnReloaded() {
        const self = this;

        self.IHaveModelPredictors_updateProperties();
    }

    private IHaveModelPredictors_updateProperties() {
        const self = this;
        self._ModelPredictorUIOptions = {};
        self._ModelPredictors = [];
        const dctModelPredictors: { [id: number]: WB_ModelPredictorRow } = {};
        _.forEach(self.WB_Model.WB_ModelPredictorRelationshipRows, (aRow) => {
            if (!aRow.isActive) {
                return;
            }
            if (aRow.WB_ModelPredictorRow) {
                const wbModelPredictorId = aRow.WB_ModelPredictorRow.cif.WB_MODEL_PREDICTOR_ID;
                dctModelPredictors[wbModelPredictorId] = aRow.WB_ModelPredictorRow;
                self._ModelPredictorUIOptions[wbModelPredictorId] = {
                    UIGroupName: aRow.cif.UI_GROUP_NAME,
                    ItemSortOrder: aRow.cif.ITEM_SORT_ORDER
                };
            }
        });
        self._ModelPredictors = _.values(dctModelPredictors);

        self.OnModelPredictorsChanged.next(self);
    }

    get bIHaveModelPredictorsLoaded(): boolean {
        return this._bIHaveModelPredictorsLoaded;
    }

    get OnIHaveModelPredictorsLoaded(): Promise<void> {
        const self = this;
        // Add Load Logic..
        if (self._bIHaveModelPredictorsLoaded) {
            return ASync.resolve();
        }

        if (self._OnIHaveModelPredictorsLoaded) {
            return self._OnIHaveModelPredictorsLoaded;
        }

        const returnValue = new Promise<void>((resolve, error) => {
            self.IHaveModelPredictors_updateProperties();

            self._bIHaveModelPredictorsLoaded = true;
            self._OnIHaveModelPredictorsLoaded = undefined;
            resolve();
        });

        self._OnIHaveModelPredictorsLoaded = returnValue;

        return returnValue;
    }

    get ModelPredictors(): WB_ModelPredictorRow[] {
        return this._ModelPredictors;
    }

    get ModelPredictorUIOptions(): { [p: number]: { UIGroupName: string; ItemSortOrder: number } } {
        return this._ModelPredictorUIOptions;
    }

    get OnModelPredictorsChanged(): BehaviorSubject<IHaveModelPredictors> {
        return this._OnModelPredictorsChanged;
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveModelTags operations">

    private _ModelTagUIOptions: { [wbTagId: number]: { UIGroupName: string; ItemSortOrder: number } };
    private _ModelTags: WB_TagRow[];
    private _OnIHaveModelTagsLoaded: Promise<void>;
    private _OnModelTagsChanged: BehaviorSubject<IHaveModelTags>;
    private _bIHaveModelTagsLoaded: boolean;

    private IHaveModelTags_init() {
        //
        this._bIHaveModelTagsLoaded = false;
        this._ModelTagUIOptions = {};
        this._ModelTags = [];
        this._OnIHaveModelTagsLoaded = undefined;
        this._OnModelTagsChanged = new BehaviorSubject<IHaveModelTags>(undefined);
    }

    private IHaveModelTags_OnReloaded() {
        const self = this;

        self.IHaveModelTags_updateProperties();
    }

    private IHaveModelTags_updateProperties() {
        const self = this;

        self._ModelTagUIOptions = {};
        self._ModelTags = [];
        const dctModelTags: { [id: number]: WB_TagRow } = {};
        _.forEach(self.WB_Model.WB_ModelTagRelationshipRows, (aRow) => {
            if (!aRow.isActive) {
                return;
            }
            if (aRow.WB_TagRow) {
                const wbTagId = aRow.WB_TagRow.cif.WB_TAG_ID;
                dctModelTags[wbTagId] = aRow.WB_TagRow;
                self._ModelTagUIOptions[wbTagId] = {
                    UIGroupName: aRow.cif.UI_GROUP_NAME,
                    ItemSortOrder: aRow.cif.ITEM_SORT_ORDER
                };
            }
        });
        self._ModelTags = _.values(dctModelTags);

        self.OnModelTagsChanged.next(self);
    }

    get bIHaveModelTagsLoaded(): boolean {
        return this._bIHaveModelTagsLoaded;
    }

    get OnIHaveModelTagsLoaded(): Promise<void> {
        const self = this;
        // Add Load Logic..
        if (self._bIHaveModelTagsLoaded) {
            return ASync.resolve();
        }

        if (self._OnIHaveModelTagsLoaded) {
            return self._OnIHaveModelTagsLoaded;
        }

        const returnValue = new Promise<void>((resolve, error) => {
            self.IHaveModelTags_updateProperties();

            self._bIHaveModelTagsLoaded = true;
            self._OnIHaveModelTagsLoaded = undefined;
        });

        self._OnIHaveModelTagsLoaded = returnValue;
    }

    get ModelTags(): WB_TagRow[] {
        return this._ModelTags;
    }

    get ModelTagUIOptions(): { [p: number]: { UIGroupName: string; ItemSortOrder: number } } {
        return this._ModelTagUIOptions;
    }

    get OnModelTagsChanged(): BehaviorSubject<IHaveModelTags> {
        return this._OnModelTagsChanged;
    }

    //</editor-fold>

    //<editor-fold desc = "TermComponent operations">

    private _OnModelComponentsChanged: BehaviorSubject<ModelBO>;

    TermComponents_init() {
        const self = this;

        this._OnModelComponentsChanged = new BehaviorSubject<ModelBO>(self);
        // Register to listen to event..
        self.OnModelTermCollectionChanged.subscribe((mdlBO) => {
            if (_.isEmpty(mdlBO)) {
                return;
            }
            if (self.bIsLoaded) self.maintainModelComponents();
        });
    }

    get OnModelComponentsChanged(): BehaviorSubject<ModelBO> {
        return this._OnModelComponentsChanged;
    }

    async maintainModelComponents(): Promise<void> {
        const self = this;

        const dctPredictorId: { [wbPredictorId: number]: number } = {};
        const dctCoeffId: { [wbCoeffId: number]: number } = {};
        const dctConstantId: { [wbConstantId: number]: number } = {};

        for (const aTermRelRow of self.drWB_Model.WB_ModelTermRelationshipRows) {
            if (!aTermRelRow.isActive) {
                continue;
            }

            if (!aTermRelRow.WB_ModelTermRow) {
                continue;
            }

            const aTerm = aTermRelRow.WB_ModelTermRow;
            const aFunction = aTerm.WB_ModelFunctionRow;

            _.forEach(
                ["WB_MODEL_PREDICTOR_ID", "WB_MODEL_PREDICTOR_ID2", "WB_MODEL_PREDICTOR_ID3", "WB_MODEL_PREDICTOR_ID4"],
                (aFieldName, idx) => {
                    const wbPredictorId = _.toNumber((<any>aTerm)[aFieldName]);
                    if (idx >= aFunction.cif.PREDICTOR_COUNT) return;

                    if (wbPredictorId > 0) {
                        dctPredictorId[wbPredictorId] = wbPredictorId;
                    }
                }
            );

            _.forEach(
                [
                    "WB_MODEL_COEFFICIENT_ID",
                    "WB_MODEL_COEFFICIENT_ID2",
                    "WB_MODEL_COEFFICIENT_ID3",
                    "WB_MODEL_COEFFICIENT_ID4"
                ],
                (aFieldName, idx) => {
                    const wbCoeffId = _.toNumber((<any>aTerm)[aFieldName]);
                    if (idx >= aFunction.cif.COEFFICIENT_COUNT) return;

                    if (wbCoeffId > 0) {
                        dctCoeffId[wbCoeffId] = wbCoeffId;
                    }
                }
            );

            _.forEach(
                ["WB_MODEL_CONSTANT_ID", "WB_MODEL_CONSTANT_ID2", "WB_MODEL_CONSTANT_ID3", "WB_MODEL_CONSTANT_ID4"],
                (aFieldName, idx) => {
                    const wbConstantId = _.toNumber((<any>aTerm)[aFieldName]);
                    if (idx >= aFunction.cif.CONSTANT_COUNT) return;

                    if (wbConstantId > 0) {
                        dctConstantId[wbConstantId] = wbConstantId;
                    }
                }
            );
        }

        const arrWBPredictorRow: WB_ModelPredictorRow[] = <any>(
            await self.dbContext.findUsingCache(esWB_Model_Predictor, _.values(dctPredictorId))
        );

        //-- Determine the Tags
        const dctTagId: { [wbTagId: number]: number } = {};
        _.forEach(arrWBPredictorRow, (aRow: WB_ModelPredictorRow) => {
            if (aRow.cif.MODEL_PREDICTOR_TYPE != "ObservationTag") return;
            if (aRow.cif.WB_TAG_ID > 0) dctTagId[aRow.cif.WB_TAG_ID] = aRow.cif.WB_TAG_ID;
        });

        const arrWBTagRow: WB_TagRow[] = <any>await self.dbContext.findUsingCache(esWB_Tag, _.values(dctTagId));

        const dctPredictors = _.keyBy(arrWBPredictorRow, (a) => a.cif.WB_MODEL_PREDICTOR_ID);
        const dctTags = _.keyBy(arrWBTagRow, (a) => a.cif.WB_TAG_ID);

        //-- TODO
        //-- Iterate over Cannibalization Coefficients to determine the Cannibalization Coefficients..
        //--  Check that no new Predictors exist, and that cannibalization coefficients are unique
        //  TODO

        const dctPredictorIdExists: { [wbPredictorId: number]: number } = {};
        const dctTagIdExists: { [wbTagId: number]: number } = {};

        const dctPredictorsMissing = _.clone(dctPredictors);
        const dctTagsMissing = _.clone(dctTags);

        // Predictors
        // Find & Delete Duplicates and Extras
        _.forEach(self.drWB_Model.WB_ModelPredictorRelationshipRows, (aRow) => {
            if (!aRow.isActive) {
                return;
            }
            const wbPredictorId = aRow.cif.WB_MODEL_PREDICTOR_ID;
            if (wbPredictorId in dctPredictorIdExists) {
                // Remove dups
                aRow.markDeleted();
                return;
            }
            if (wbPredictorId in dctPredictors) {
                delete dctPredictorsMissing[wbPredictorId];
                dctPredictorIdExists[wbPredictorId] = wbPredictorId;
            } else {
                aRow.markDeleted();
            }
        });
        // Add Missing
        for (const aPredictorRow of _.values(dctPredictorsMissing)) {
            const newRow: WB_ModelPredictorRelationshipRow = <any>(
                await self.dbContext.createRecordWithKey(esWB_Model_Predictor_Relationship)
            );
            newRow.setDefaults();
            newRow.cif.WB_MODEL_ID = self.drWB_Model.cif.WB_MODEL_ID;
            newRow.WB_ModelRow = self.drWB_Model;
            newRow.cif.WB_MODEL_PREDICTOR_ID = aPredictorRow.cif.WB_MODEL_PREDICTOR_ID;
            newRow.WB_ModelPredictorRow = aPredictorRow;
            self.dbContext.add(esWB_Model_Predictor_Relationship, newRow);
        }

        // Tags
        // Find & Delete Duplicates and Extras
        _.forEach(self.drWB_Model.WB_ModelTagRelationshipRows, (aRow) => {
            if (!aRow.isActive) {
                return;
            }
            const wbTagId = aRow.cif.WB_TAG_ID;
            if (wbTagId in dctTagIdExists) {
                // Remove dups
                aRow.markDeleted();
                return;
            }
            if (wbTagId in dctTags) {
                delete dctTagsMissing[wbTagId];
                dctTagIdExists[wbTagId] = wbTagId;
            } else {
                aRow.markDeleted();
            }
        });
        // Add Missing
        for (const aTagRow of _.values(dctTagsMissing)) {
            const newRow: WB_ModelTagRelationshipRow = <any>(
                await self.dbContext.createRecordWithKey(esWB_Model_Tag_Relationship)
            );
            newRow.setDefaults();
            newRow.cif.WB_MODEL_ID = self.drWB_Model.cif.WB_MODEL_ID;
            newRow.WB_ModelRow = self.drWB_Model;
            newRow.cif.WB_TAG_ID = aTagRow.cif.WB_TAG_ID;
            newRow.WB_TagRow = aTagRow;
            self.dbContext.add(esWB_Model_Tag_Relationship, newRow);
        }

        //For Volume Component ItemR

        for (const aVolumeComponentRelRow of self.drWB_Model.WB_ModelVolumeComponentRelationshipRows) {
            if (!aVolumeComponentRelRow.isActive) continue;

            const dctPredictorIdExists: { [wbPredictorId: number]: number } = {};
            const dctPredictorsMissing = _.clone(dctPredictors);

            // Find Missing, remove dups
            for (const aItemRow of aVolumeComponentRelRow.WB_ModelVolumeComponentItemRows) {
                if (!aItemRow.isActive) {
                    continue;
                }

                const wbPredictorId = aItemRow.cif.WB_MODEL_PREDICTOR_ID;
                if (wbPredictorId in dctPredictorIdExists) {
                    // Remove dups
                    aItemRow.markDeleted();
                    continue;
                }

                if (wbPredictorId in dctPredictors) {
                    delete dctPredictorsMissing[wbPredictorId];
                    dctPredictorIdExists[wbPredictorId] = wbPredictorId;
                } else {
                    aItemRow.markDeleted();
                }
            }

            for (const aPredictorRow of _.values(dctPredictorsMissing)) {
                const aNewRow: WB_ModelVolumeComponentItemRow = <any>(
                    await self.dbContext.createRecordWithKey(esWB_Model_Volume_Component_ItemR)
                );
                aNewRow.setDefaults();
                aNewRow.cif.WB_MODEL_VOLUME_COMPONENTR_ID = aVolumeComponentRelRow.cif.WB_MODEL_VOLUME_COMPONENTR_ID;
                aNewRow.WB_ModelVolumeComponentRelationshipRow = aVolumeComponentRelRow;
                aNewRow.cif.WB_MODEL_PREDICTOR_ID = aPredictorRow.cif.WB_MODEL_PREDICTOR_ID;
                aNewRow.cif.WB_MODEL_PREDICTOR_ID_SRC = 0;
                aNewRow.cif.VALUE_TYPE = "self";
                self.dbContext.add(esWB_Model_Volume_Component_ItemR, aNewRow);
            }
        }

        self.OnModelComponentsChanged.next(self);
    }

    //</editor-fold>

    //<editor-fold desc = "Event Type functionality">
    private _OnEventTypesChanged: BehaviorSubject<ModelBO>;

    private IHaveEventTypes_init() {
        this._OnEventTypesChanged = new BehaviorSubject<ModelBO>(undefined)
    }

    get OnEventTypesChanged(): BehaviorSubject<ModelBO> {
        return this._OnEventTypesChanged
    }

    async addEventType(): Promise<WB_ModelEventTypeRelationshipRow> {
        const self = this;

        // create new records
        const aNewRow = <WB_ModelVolumeComponentRelationshipRow>await self.dbContext.createRecordWithKey(esWB_Model_Event_Type_Relationship);
        aNewRow.setDefaults();
        aNewRow.cif.WB_MODEL_ID = self.drWB_Model.cif.WB_MODEL_ID;
        aNewRow.WB_ModelRow = self.drWB_Model;
        self.dbContext.add(esWB_Model_Event_Type_Relationship, aNewRow);
        self.OnEventTypesChanged.next(self);
        return aNewRow;
    }

    async removeEventType(aWbId: number): Promise<void> {
        const self = this;
        _.forEach(self.drWB_Model.WB_ModelEventTypeRelationshipRows,
            (aRow: WB_ModelEventTypeRelationshipRow) => {
                if (
                    aRow.isActive &&
                    (aRow.cif.WB_MODEL_EVTTYPER_ID == aWbId)
                ) {
                    aRow.markDeleted();
                }
            }
        );

        self.OnEventTypesChanged.next(self);
    }

    //</editor-fold>

    //<editor-fold desc = "addVolumeComponent and removeVolumeComponentRelationshipRow functionality">

    private _OnVolumeComponentsChanged: BehaviorSubject<ModelBO>;

    private IHaveVolumeComponents_init() {
        this._OnVolumeComponentsChanged = new BehaviorSubject<ModelBO>(undefined)
    }

    get OnVolumeComponentsChanged(): BehaviorSubject<ModelBO> {
        return this._OnVolumeComponentsChanged
    }

    async addVolumeComponent(aWbModelVolumeComponentId: number): Promise<WB_ModelVolumeComponentRelationshipRow> {
        const self = this;

        for (const aVCRow of self.WB_Model.WB_ModelVolumeComponentRelationshipRows) {
            if (!aVCRow.isActive)
                continue;

            // Prevent Duplicates
            if (aVCRow.cif.WB_MODEL_VOLUME_COMPONENT_ID == aWbModelVolumeComponentId)
                return aVCRow
        }

        const aModelVolumeComponentRow: WB_ModelVolumeComponentRow = await self.dbContext.find(esWB_Model_Volume_Component, aWbModelVolumeComponentId)
        if (!aModelVolumeComponentRow)
            return;

        // create new records
        const aNewRow = <WB_ModelVolumeComponentRelationshipRow>await self.dbContext.createRecordWithKey(esWB_Model_Volume_ComponentR);
        aNewRow.setDefaults();
        aNewRow.cif.WB_MODEL_ID = self.drWB_Model.cif.WB_MODEL_ID;
        aNewRow.WB_ModelRow = self.drWB_Model;
        aNewRow.cif.WB_MODEL_VOLUME_COMPONENT_ID = aModelVolumeComponentRow.cif.WB_MODEL_VOLUME_COMPONENT_ID;
        aNewRow.WB_ModelVolumeComponentRow = aModelVolumeComponentRow;
        self.dbContext.add(esWB_Model_Volume_ComponentR, aNewRow);
        self.maintainModelComponents();
        self.OnVolumeComponentsChanged.next(self);
        return aNewRow;
    }

    async createVolumeComponent(sId: string, sName: string, sTypeId: string, sSubTypeId: string): Promise<WB_ModelVolumeComponentRelationshipRow> {
        const self = this;

        const vcRow = <WB_ModelVolumeComponentRow>await self.dbContext.createRecordWithKey(esWB_Model_Volume_Component)
        vcRow.cif.VOLUME_COMPONENT_ID = sId
        vcRow.cif.VOLUME_COMPONENT_NAME = sName
        vcRow.cif.VOLUME_COMPONENT_TYPE = sTypeId
        vcRow.cif.VOLUME_COMPONENT_SUBTYPE = sSubTypeId
        self.dbContext.add(esWB_Model_Volume_Component, vcRow)

        await self.dbContext.Save(esWB_Model_Volume_Component, vcRow)
        return await self.addVolumeComponent(vcRow.cif.WB_MODEL_VOLUME_COMPONENT_ID)
    }

    async removeVolumeComponentRelationshipRow(aWbVolumeComponentRelationshipId: number): Promise<void> {
        const self = this;
        _.forEach(self.drWB_Model.WB_ModelVolumeComponentRelationshipRows,
            (aVolumeComponentRelationshipRow: WB_ModelVolumeComponentRelationshipRow) => {
                if (
                    aVolumeComponentRelationshipRow.isActive &&
                    (aVolumeComponentRelationshipRow.cif.WB_MODEL_VOLUME_COMPONENTR_ID == aWbVolumeComponentRelationshipId)
                ) {
                    aVolumeComponentRelationshipRow.markDeleted();
                    aVolumeComponentRelationshipRow.off(); // Turn off Event Generation
                    _.forEach(aVolumeComponentRelationshipRow.WB_ModelVolumeComponentItemRows, (anItemRow) => {
                        anItemRow.markDeleted();
                        anItemRow.off();
                    });
                }
            }
        );

        self.maintainModelComponents();
        self.OnVolumeComponentsChanged.next(self);
    }

    getVolumeComponentFirstObservedPredictors(): { [wbVolumeComponentId: number]: WB_ModelPredictorRow[] } {
        const self = this

        const dctPred: { [wbPredictorId: number]: WB_ModelPredictorRow } = {}
        for (const r of self.WB_Model.WB_ModelPredictorRelationshipRows) {
            if (!r.isActive)
                return
            if (r.WB_ModelPredictorRow)
                dctPred[r.WB_ModelPredictorRow.cif.WB_MODEL_PREDICTOR_ID] = r.WB_ModelPredictorRow
        }
        const rv: { [wbVolumeComponentId: number]: WB_ModelPredictorRow[] } = {}

        let vcRels = _.filter(self.WB_Model.WB_ModelVolumeComponentRelationshipRows, (rel) => rel.isActive)
        vcRels = _.orderBy(vcRels, (rel) => rel.cif.DECOMPOSITION_ORDER)
        const dctPredExists: { [wbPredictorId: number]: WB_ModelPredictorRow } = {}

        for (const rel of vcRels) {
            const wbVolCompId = rel.cif.WB_MODEL_VOLUME_COMPONENT_ID
            if (wbVolCompId in rv)
                return

            rv[wbVolCompId] = []

            for (const pRel of rel.WB_ModelVolumeComponentItemRows) {
                if (!pRel.isActive)
                    continue

                if (pRel.cif.VALUE_TYPE == "self") {
                    if (pRel.cif.WB_MODEL_PREDICTOR_ID in dctPredExists)
                        continue

                    dctPredExists[pRel.cif.WB_MODEL_PREDICTOR_ID] = dctPred[pRel.cif.WB_MODEL_PREDICTOR_ID]

                    rv[wbVolCompId].push(dctPred[pRel.cif.WB_MODEL_PREDICTOR_ID])
                }
            } // Next Volume Componnet Item

        } // Next Volume Component

        return rv
    }

    //</editor-fold>

    //<editor-fold desc = "model profiles">

    private _OnProfileCollectionChanged: BehaviorSubject<ModelBO>;
    private _OnProfilePropertyChanged: BehaviorSubject<IModelProfileChanged>;

    private IHaveProfiles_init() {
        this._OnProfileCollectionChanged = new BehaviorSubject<ModelBO>(undefined)
        this._OnProfilePropertyChanged = new BehaviorSubject<IModelProfileChanged>(undefined)
    }

    get OnProfileCollectionChanged(): BehaviorSubject<ModelBO> {
        return this._OnProfileCollectionChanged
    }

    get OnProfilePropertyChanged(): BehaviorSubject<IModelProfileChanged> {
        return this._OnProfilePropertyChanged
    }

    private async IHaveProfiles_onLoadCompelte(): Promise<void> {
        const self = this
        // Wire up Change Events
        _.forEach(self.WB_Model.WB_ModelProfileRows, (aRow) => {
            aRow.setupOnChangeEvents([], self.IProfile_onRecordChange.bind(self), self);
        })
    }

    private IProfile_onRecordChange(record: WB_ModelProfileRow, propertyName: string, propertyValue: any, options: ModelBO) {
        this._OnProfilePropertyChanged.next({
            ModelBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        });
    }

    async syncProfilePredictors(): Promise<void> {
        const self = this;
        for (const row of self.WB_Model.WB_ModelProfileRows) {
            if (!row.isActive) return;

            const predictors = _.map(self.ModelPredictors, "WB_MODEL_PREDICTOR_ID");
            const profilePredictors = _.map(row.WB_ModelProfilePredictorRows, (row) => {
                if (row.isActive) return row.cif.WB_MODEL_PREDICTOR_ID;
            });

            const missingIds = _.difference(predictors, profilePredictors);
            const removingIds = _.difference(profilePredictors, predictors);
            if (!_.isEmpty(missingIds)) await self.addProfilePredictors(row, { add: missingIds, remove: removingIds });

            if (!_.isEmpty(removingIds)) {
                const removeProws = _.filter(row.WB_ModelProfilePredictorRows, (predictor) =>
                    _.includes(removingIds, predictor.cif.WB_MODEL_PREDICTOR_ID)
                );
                _.forEach(removeProws, (rem) => {
                    rem.markDeleted();
                });

                _.forEach(row.WB_ModelProfileGroupRows, (group) =>
                    _.forEach(group.WB_ModelProfileGroupPredictorRows, (predictor) => {
                        if (predictor.isActive && _.includes(removingIds, predictor.cif.WB_MODEL_PREDICTOR_ID))
                            predictor.markDeleted();
                    })
                );
            }
        }
    }

    async addProfile(wbModelEvtTypeRId?: number, sProfileType?: string, bDefault?: boolean): Promise<WB_ModelProfileRow> {
        const self = this;
        const sProfileTypeId = sProfileType || ""
        wbModelEvtTypeRId = wbModelEvtTypeRId || 0

        if (bDefault) {
            // Only one default is allowed by Event Type
            const exists = _.filter(self.WB_Model.WB_ModelProfileRows, (a) =>
                a.isActive &&
                (a.cif.DEFAULT_IND == '1') &&
                (a.cif.WB_MODEL_EVTTYPER_ID == wbModelEvtTypeRId) &&
                (a.cif.PROFILE_TYPE == sProfileTypeId))
            if (exists.length) {
                const rv = exists[0]
                return rv
            }
        }

        // create new records
        const aNewRow: WB_ModelProfileRow = <any>await self.dbContext.createRecordWithKey(esWB_Model_Profile);
        if (!aNewRow) {
            return;
        }

        aNewRow.setDefaults();
        aNewRow.WB_ModelRow = self.WB_Model
        aNewRow.cif.WB_MODEL_ID = self.WB_Model.cif.WB_MODEL_ID;
        aNewRow.cif.PROFILE_TYPE = sProfileTypeId;
        aNewRow.cif.VOLUME_TYPE = "Total"; // Legacy
        aNewRow.cif.DEFAULT_IND = (bDefault) ? '1' : '0'
        aNewRow.cif.WB_MODEL_EVTTYPER_ID = wbModelEvtTypeRId || 0
        await self.addProfilePredictors(aNewRow);
        self.dbContext.add(esWB_Model_Profile, aNewRow);
        self.OnProfileCollectionChanged.next(self);
        aNewRow.setupOnChangeEvents([], self.IProfile_onRecordChange.bind(self), self);
        return aNewRow;
    }

    async removeProfile(aWbModelProfileId: number): Promise<void> {
        const self = this;

        _.forEach(self.WB_Model.WB_ModelProfileRows, (aRow: WB_ModelProfileRow) => {
            if (aRow.isActive && aRow.cif.WB_MODEL_PROFILE_ID == aWbModelProfileId) {
                aRow.markDeleted();
                _.forEach(aRow.WB_ModelProfilePredictorRows, (predictor) => {
                    predictor.markDeleted();
                });
            }
        });

        self.OnProfileCollectionChanged.next(self);

    }

    async createDefaultProfiles(): Promise<void> {
        const self = this;

        const dctVCPredictors = self.getVolumeComponentFirstObservedPredictors()

        const fncs = <WB_SpendFunctionRow[]>await self.dbContext.findAll(esWB_Spend_Function, {
            where: {
                PROFILE_DEFAULT_IND: '1',
                DELETED_IND: { neq: '1' }
            }
        })

        const dfltSpendFunction = _.filter(fncs, (f) => f.cif.FUNCTION_TYPE == 'Spend')
        const dfltShipFunction = _.filter(fncs, (f) => f.cif.FUNCTION_TYPE == 'Shipment')

        const ProfileTypes = ['Simulator', 'Optimizer']
        const mdlEvtTypes = _.filter(self.WB_Model.WB_ModelEventTypeRelationshipRows, (a) => a.isActive)
        const dctEvtTypes = _.keyBy(mdlEvtTypes, (et) => et.cif.WB_MODEL_EVTTYPER_ID)

        // For Each Event Type, Profile Type
        for (const aProfileTypeId of ProfileTypes) {
            for (const aEvtType of mdlEvtTypes) {

                const arrProfilePredictors = dctVCPredictors[aEvtType.cif.WB_MODEL_VOLUME_COMPONENT_ID]
                if (_.isEmpty(arrProfilePredictors))
                    continue;

                const profile = await self.addProfile(aEvtType.cif.WB_MODEL_EVTTYPER_ID, aProfileTypeId, true);

                if (profile.isActiveNew && _.isEmpty(profile.cif.PROFILE_NAME)) {
                    profile.cif.PROFILE_NAME = `${aEvtType.cif.TYPE_NAME} Default Profile`


                    // Find Spend Functions and Shipment Functions with default set to true
                    for (const spndFnc of dfltSpendFunction) {
                        await self.addSpendFunction(spndFnc, profile.cif.WB_MODEL_PROFILE_ID)
                    }

                    for (const shipFnc of dfltShipFunction) {
                        await self.addSpendFunction(shipFnc, profile.cif.WB_MODEL_PROFILE_ID)
                    }

                }
            }
        }

        await self.syncProfilePredictors()

        // Create groups for Default Profiles
        const dfltProfiles = _.filter(self.WB_Model.WB_ModelProfileRows, (mp) =>
            mp.isActive && (mp.cif.DEFAULT_IND == '1') && (mp.cif.WB_MODEL_EVTTYPER_ID in dctEvtTypes))

        for (const prfl of dfltProfiles) {
            const evtType = dctEvtTypes[prfl.cif.WB_MODEL_EVTTYPER_ID]
            const arrPredictors = dctVCPredictors[evtType.cif.WB_MODEL_VOLUME_COMPONENT_ID]

            if (_.isEmpty(arrPredictors))
                continue;

            const grps = _.filter(prfl.WB_ModelProfileGroupRows, (a) => a.isActive)
            if (grps.length)
                continue

            const grp = await self.addGroup(prfl.cif.WB_MODEL_PROFILE_ID)
            if (grp) {
                grp.cif.GROUP_NAME = `Predictor Inputs`
                for (const pred of arrPredictors) {
                    await self.addGroupPredictor(prfl.cif.WB_MODEL_PROFILE_ID, grp.cif.WB_MODEL_PROFILEGR_ID, pred.cif.WB_MODEL_PREDICTOR_ID)
                }
            }
        }


    }

    //</editor-fold>

    //<editor-fold desc = "Profile predictor operations">
    get dctModelPredictors(): { [type: string]: { predictor: WB_ModelPredictorRow; source: string }[] } {
        const dct: { [type: string]: { predictor: WB_ModelPredictorRow; source: string }[] } = {};
        _.forEach(this.WB_Model.WB_ModelVolumeComponentRelationshipRows, (row) => {
            if (!(row.WB_ModelVolumeComponentRow.cif.VOLUME_COMPONENT_TYPE in dct)) {
                dct[row.WB_ModelVolumeComponentRow.cif.VOLUME_COMPONENT_TYPE] = [];
            }
            _.forEach(row.WB_ModelVolumeComponentItemRows, (item) => {
                const predictor = _.first(
                    _.filter(
                        this.ModelPredictors,
                        (predictor) => predictor.cif.WB_MODEL_PREDICTOR_ID == item.cif.WB_MODEL_PREDICTOR_ID
                    )
                );
                if (!_.isEmpty(predictor))
                    dct[row.WB_ModelVolumeComponentRow.cif.VOLUME_COMPONENT_TYPE].push({
                        predictor: predictor,
                        source: ComponentTypeToSourceMapping[item.cif.VALUE_TYPE]
                    });
            });

            if (_.isEmpty(dct[row.WB_ModelVolumeComponentRow.cif.VOLUME_COMPONENT_TYPE])) {
                delete dct[row.WB_ModelVolumeComponentRow.cif.VOLUME_COMPONENT_TYPE];
            }
        });
        return dct;
    }

    private _OnProfilePredictorCollectionChanged: BehaviorSubject<ModelBO>;

    ProfilePredictor_init() {
        const self = this;
        this._OnProfilePredictorCollectionChanged = new BehaviorSubject<ModelBO>(self);
    }

    get OnProfilePredictorCollectionChanged(): BehaviorSubject<ModelBO> {
        return this._OnProfilePredictorCollectionChanged;
    }

    //</editor-fold>

    //<editor-fold desc = "addProfilePredictor  and removeProfilePredictor functionality">
    addProfilePredictors(profile: WB_ModelProfileRow, opts?: any): Promise<ModelBO> {
        const self = this;
        let keyCount = self.ModelPredictors.length;
        let predictors = self.ModelPredictors;
        if (!_.isEmpty(opts) && "add" in opts) {
            keyCount = opts.add.length;
            predictors = _.filter(predictors, (row) => _.includes(opts.add, row.cif.WB_MODEL_PREDICTOR_ID));
        }

        return new Promise<ModelBO>((resolve, error) => {
            self.dbContext
                .getNewIds(esWB_Model_Profile_Predictor, keyCount)
                .then((keys: number[]) => {
                    _.forEach(predictors, (predictor: WB_ModelPredictorRow, i: number) => {
                        const aNewRow: WB_ModelProfilePredictorRow = <WB_ModelProfilePredictorRow>(
                            self.dbContext.createRecord(esWB_Model_Profile_Predictor)
                        );

                        aNewRow.setDefaults();
                        aNewRow.cif.WB_MODEL_PROFILEPR_ID = keys[i];

                        aNewRow.WB_ModelPredictorRow = predictor;
                        aNewRow.cif.WB_MODEL_PREDICTOR_ID = aNewRow.WB_ModelPredictorRow.cif.WB_MODEL_PREDICTOR_ID;

                        aNewRow.WB_ModelProfileRow = profile;
                        aNewRow.cif.WB_MODEL_PROFILE_ID = profile.cif.WB_MODEL_PROFILE_ID;

                        aNewRow.cif.INPUT_METHOD = "UserEntered";

                        self.dbContext.add(esWB_Model_Profile_Predictor, aNewRow);
                    });

                    self.OnProfilePredictorCollectionChanged.next(self);
                    resolve(self);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //</editor-fold>
    //<editor-fold desc = "model Profile groups">

    private _onProfileGroupCollectionChanged: BehaviorSubject<ModelBO>;

    ProfileGroupCollection_init() {
        const self = this;
        this._onProfileGroupCollectionChanged = new BehaviorSubject<ModelBO>(undefined);
    }

    get onProfileGroupCollectionChanged(): BehaviorSubject<ModelBO> {
        return this._onProfileGroupCollectionChanged;
    }

    async addGroup(selectedProfileId?: any): Promise<WB_ModelProfileGroupRow> {
        const self = this;

        const profileRow = _.first(
            _.filter(
                self.drWB_Model.WB_ModelProfileRows,
                (row) => row.isActive && row.cif.WB_MODEL_PROFILE_ID == selectedProfileId
            )
        );
        const groups = _.filter(profileRow.WB_ModelProfileGroupRows, (group) => group.isActive);
        // create new records
        const aNewRow: WB_ModelProfileGroupRow = <any>await self.dbContext.createRecordWithKey(esWB_Model_Profile_Group);
        aNewRow.setDefaults();
        aNewRow.cif.WB_MODEL_PROFILE_ID = selectedProfileId;
        aNewRow.cif.WIDGET_TYPE = profileRow.cif.PROFILE_TYPE === "Simulator" ? "HyperRectangle" : "Predictor";
        aNewRow.cif.PRD_IND = aNewRow.cif.WIDGET_TYPE == "HyperRectangle" ? "1" : "0";
        aNewRow.cif.GROUP_SORT = groups.length + 1;
        self.dbContext.add(esWB_Model_Profile_Group, aNewRow);

        self.onProfileGroupCollectionChanged.next(self);

        return aNewRow;
    }

    async removeGroup(aWbModelProfileGroupId: number, selectedProfileId?: any): Promise<void> {
        const self = this;
        _.forEach(self.drWB_Model.WB_ModelProfileRows, (row) => {
            if (row.cif.WB_MODEL_PROFILE_ID == selectedProfileId) {
                _.forEach(row.WB_ModelProfileGroupRows, (aRow: WB_ModelProfileGroupRow) => {
                    if (aRow.isActive && aRow.cif.WB_MODEL_PROFILEGR_ID == aWbModelProfileGroupId) {
                        aRow.markDeleted();
                    }
                });
            }
        });
        self.onProfileGroupCollectionChanged.next(self);
    }

    async addGroupPredictor(
        wbProfileId: number,
        wbProfileGroupId: number,
        wbPredictorId: number
    ): Promise<WB_ModelProfileGroupPredictorRow> {
        const self = this;

        let found = false;
        let idx = 0;
        // create new records

        _.forEach(self.drWB_Model.WB_ModelProfileRows, (aRow) => {
            if (aRow.cif.WB_MODEL_PROFILE_ID == wbProfileId) {
                _.forEach(aRow.WB_ModelProfileGroupRows, (profileGroup: WB_ModelProfileGroupRow) => {
                    if (profileGroup.cif.WB_MODEL_PROFILEGR_ID == wbProfileGroupId) {
                        _.forEach(
                            profileGroup.WB_ModelProfileGroupPredictorRows,
                            (predictorRows: WB_ModelProfileGroupPredictorRow) => {
                                if (
                                    wbPredictorId == predictorRows.cif.WB_MODEL_PREDICTOR_ID &&
                                    predictorRows.isActive
                                ) {
                                    found = true;
                                    return false;
                                }
                            }
                        );
                    }
                });
            }
        });

        if (found) {
            return;
        }

        const result: WB_ModelPredictorRow = await self.dbContext.find(esWB_Model_Predictor, wbPredictorId);
        if (_.isEmpty(result)) return;

        const aNewRow: WB_ModelProfileGroupPredictorRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Model_Profile_Group_Predictor)
        );
        aNewRow.setDefaults();
        aNewRow.WB_ModelProfileGroupRow = _.first(_.filter(_.first(_.filter(self.WB_Model.WB_ModelProfileRows, r =>r.isActive && r.cif.WB_MODEL_PROFILE_ID == wbProfileId))?.WB_ModelProfileGroupRows, r => r.isActive && r.cif.WB_MODEL_PROFILEGR_ID == wbProfileId));
        aNewRow.cif.WB_MODEL_PROFILEGR_ID = wbProfileGroupId;
        aNewRow.cif.SORT_ORDER = idx + 1;
        aNewRow.cif.WB_MODEL_PREDICTOR_ID = wbPredictorId;
        aNewRow.WB_ModelPredictorRow = result;
        aNewRow.cif.PREDICTOR_LABEL = result.cif.MODEL_PREDICTOR_NAME;
        self.dbContext.add(esWB_Model_Profile_Group_Predictor, aNewRow);
        _.forEach(self.drWB_Model.WB_ModelProfileRows, (aRow) => {
            if (aRow.cif.WB_MODEL_PROFILE_ID == wbProfileId) {
                _.forEach(aRow.WB_ModelProfileGroupRows, (profileGroup: WB_ModelProfileGroupRow) => {
                    if (profileGroup.cif.WB_MODEL_PROFILEGR_ID == wbProfileGroupId) {
                        _.forEach(
                            _.orderBy(
                                _.filter(profileGroup.WB_ModelProfileGroupPredictorRows, (aRow) => aRow.isActive),
                                "SORT_ORDER",
                                "asc"
                            ),
                            (aRow) => {
                                aRow.cif.SORT_ORDER = idx;
                                idx++;
                            }
                        );
                    }
                });
            }
        });
        return aNewRow;
    }

    async removeGroupPredictor(
        aWbModelProfileGroupPredictorId: number,
        selectedProfileId: any,
        groupId: any
    ): Promise<void> {
        const self = this;
        const found = false;
        let idx = 0;
        // create new records

        _.forEach(self.drWB_Model.WB_ModelProfileRows, (aRow) => {
            if (aRow.cif.WB_MODEL_PROFILE_ID == selectedProfileId) {
                _.forEach(aRow.WB_ModelProfileGroupRows, (profileGroup: WB_ModelProfileGroupRow) => {
                    if (profileGroup.cif.WB_MODEL_PROFILEGR_ID == groupId) {
                        _.forEach(
                            _.orderBy(
                                _.filter(profileGroup.WB_ModelProfileGroupPredictorRows, (aRow) => aRow.isActive),
                                "SORT_ORDER",
                                "asc"
                            ),
                            (aRow) => {
                                aRow.cif.SORT_ORDER = idx;
                                idx++;
                            }
                        );
                    }
                });
            }
        });

        _.forEach(self.drWB_Model.WB_ModelProfileRows, (aRow) => {
            if (aRow.cif.WB_MODEL_PROFILE_ID == selectedProfileId) {
                _.forEach(aRow.WB_ModelProfileGroupRows, (profileGroup: WB_ModelProfileGroupRow) => {
                    if (profileGroup.cif.WB_MODEL_PROFILEGR_ID == groupId) {
                        _.forEach(
                            profileGroup.WB_ModelProfileGroupPredictorRows,
                            (predictorRows: WB_ModelProfileGroupPredictorRow) => {
                                if (
                                    aWbModelProfileGroupPredictorId == predictorRows.cif.WB_MODEL_PROFILEGPR_ID &&
                                    predictorRows.isActive
                                ) {
                                    predictorRows.markDeleted();
                                }
                            }
                        );
                    }
                });
            }
        });
    }

    async addSpendFunction(
        aSpendFunction: WB_SpendFunctionRow,
        selectedProfileId?: any
    ): Promise<WB_ModelProfileSpendFunctionRow> {
        const self = this;

        // create new records
        let found = false;

        _.forEach(self.drWB_Model.WB_ModelProfileRows, (aRow) => {
            if (aRow.cif.WB_MODEL_PROFILE_ID == selectedProfileId) {
                _.forEach(aRow.WB_ModelProfileSpendFunctionRows, (spendFunction) => {
                    if (
                        aSpendFunction.cif.WB_SPEND_FUNCTION_ID == spendFunction.cif.WB_SPEND_FUNCTION_ID &&
                        spendFunction.isActive
                    ) {
                        found = true;
                        return false;
                    }
                });
            }
        });

        if (found) return;

        const aNewRow: WB_ModelProfileSpendFunctionRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Model_Profile_Spend_Function)
        );
        aNewRow.setDefaults();
        aNewRow.cif.WB_MODEL_PROFILE_ID = selectedProfileId;
        aNewRow.cif.WB_SPEND_FUNCTION_ID = aSpendFunction.cif.WB_SPEND_FUNCTION_ID;
        aNewRow.WB_SpendFunctionRow = aSpendFunction;
        self.dbContext.add(esWB_Model_Profile_Spend_Function, aNewRow);

        return aNewRow;
    }

    //</editor-fold>

    //<editor-fold desc = "IRecordResolver operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const model_options: CreateOptions = {
            standardConditions: {
                [esWB_Model_Term_Relationship]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Model_Cannibalization_Relationship]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Model_Predictor_Relationship]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Model_Profile_Group_Predictor]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Model_Profile_Group]: { DELETED_IND: { notIn: ["1"] } },
                [esWB_Model_Profile]: { DELETED_IND: { notIn: ["1"] } }
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Model]: [esWB_Model_DataSource],
                [esWB_Model_Term]: [esWB_Model_Function],
                [esWB_Model_Cannibalization_Type]: [esWB_Model_Volume_Component]
            }
        };

        const aRecResolver = new ESRecordResolver(dbContext, model_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Model, ids)
                .then((result) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(ModelBO, "ModelBO", "Model");
